// TOUR-2 — viewport overlay: intent ring + optional Firefly bug at target.

import { useMemo } from "react";
import { createPortal } from "react-dom";
import type { FireflyIntent } from "./fireflyIntents";
import { getFireflyIntentParams } from "./fireflyIntents";
import { Firefly } from "./Firefly";
import { FireflyViewportPortal } from "./FireflyViewportPortal";
import type { FireflyTargetRect } from "@/lib/firefly/fireflyTargetConvention";
import {
  FIREFLY_TOUR_BUG_Z,
  FIREFLY_TOUR_HIGHLIGHT_RING_Z,
} from "@/lib/firefly/fireflyTourLayerZIndex";

const RING_PAD_PX = 6;

export function FireflyTargetHighlightOverlay(props: {
  rect: FireflyTargetRect;
  intent: Extract<FireflyIntent, "teaching" | "guidance" | "discovery">;
  reducedMotion: boolean;
  highlightGeneration: number;
  callout?: { title: string; body: string };
}) {
  const { rect, intent, reducedMotion, highlightGeneration, callout } = props;

  const glowColor = useMemo(() => {
    const params = getFireflyIntentParams(intent);
    return params?.glowColor ?? "rgba(52, 211, 153, 0.9)";
  }, [intent]);

  const ringStyle = {
    top: rect.top - RING_PAD_PX,
    left: rect.left - RING_PAD_PX,
    width: rect.width + RING_PAD_PX * 2,
    height: rect.height + RING_PAD_PX * 2,
    borderColor: glowColor,
  };

  const calloutTop = rect.top + rect.height + 12;
  const viewportMaxTop =
    typeof window !== "undefined" ? window.innerHeight - 120 : calloutTop;

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      <div
        aria-hidden
        className={[
          "pointer-events-none fixed rounded-lg",
          reducedMotion ? "" : "animate-pulse",
        ].join(" ")}
        style={{
          ...ringStyle,
          zIndex: FIREFLY_TOUR_HIGHLIGHT_RING_Z,
          borderWidth: 2,
          borderStyle: "solid",
          boxShadow: reducedMotion ? undefined : `0 0 0 1px ${glowColor}, 0 0 18px ${glowColor}`,
        }}
      />
      {/* Always show the bug at the target — reduced motion skips travel/pulse only. */}
      <FireflyViewportPortal
        viewportX={rect.centerX}
        viewportY={rect.centerY}
        smoothTransition={false}
        zIndex={FIREFLY_TOUR_BUG_Z}
      >
        <Firefly
          intent={intent}
          anchorX={0}
          anchorY={0}
          highlightGeneration={highlightGeneration}
          highlightX={0}
          highlightY={0}
          ambientHover={false}
        />
      </FireflyViewportPortal>
      {callout ? (
        <div
          role="status"
          className="pointer-events-none fixed max-w-xs rounded-xl border border-emerald-400/60 bg-white/95 p-3 shadow-lg backdrop-blur-sm dark:border-emerald-600/50 dark:bg-zinc-900/95"
          style={{
            zIndex: FIREFLY_TOUR_BUG_Z + 1,
            top: Math.min(calloutTop, viewportMaxTop),
            left: Math.max(
              12,
              Math.min(
                rect.centerX - 160,
                typeof window !== "undefined" ? window.innerWidth - 172 : rect.centerX - 160,
              ),
            ),
          }}
        >
          <p className="text-xs font-semibold text-emerald-900 dark:text-emerald-100">{callout.title}</p>
          <p className="mt-1 text-xs text-gray-700 dark:text-zinc-300">{callout.body}</p>
        </div>
      ) : null}
    </>,
    document.body,
  );
}
